import React, { createContext, useContext, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, AUTH_ENABLED, storedSession } from "./supabase";
import { syncAccountNameToLocal, clearAccountNameSync } from "./profile";
import { startAccountSync, stopAccountSync } from "./account-settings";
import { setPaidCosmeticsAllowed } from "./cosmetics-gate";
import { track } from "./analytics";

// What the rest of the app sees. The method signatures stay identical whether
// or not auth is configured — when disabled they're inert no-ops, so callers
// (NavBar, LoginModal) never have to branch on AUTH_ENABLED themselves.
export interface AuthContextValue {
  enabled: boolean;
  user: User | null;
  session: Session | null;
  // True until the initial session lookup resolves; UI can avoid flicker.
  loading: boolean;
  signInWithGoogle: () => Promise<{ error?: string }>;
  signInWithDiscord: () => Promise<{ error?: string }>;
  signInWithFacebook: () => Promise<{ error?: string }>;
  signInWithMagicLink: (email: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
}

const INERT: AuthContextValue = {
  enabled: false,
  user: null,
  session: null,
  loading: false,
  signInWithGoogle: async () => ({}),
  signInWithDiscord: async () => ({}),
  signInWithFacebook: async () => ({}),
  signInWithMagicLink: async () => ({}),
  signOut: async () => {},
};

const AuthContext = createContext<AuthContextValue>(INERT);

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

// Remember the last resolved auth state so the nav can render the right control
// (Sign in vs avatar) on the very first frame, before the async session check
// finishes. Without this the auth slot is blank on load and the control pops in
// a beat later (a subtle blink). getSession() is async, but this hint is sync.
const SIGNED_IN_HINT = "ar_signed_in";
const SIGNED_IN_HINT_ID = "ar_signed_in_id";
export function getSignedInHint(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SIGNED_IN_HINT) === "1";
  } catch {
    return false;
  }
}
// The last signed-in account's id (null when the last state was signed out):
// lets screens seed per-account caches (the lobby's rank badge) on the first
// frame instead of showing the signed-out state until the session resolves.
export function getSignedInHintId(): string | null {
  if (!getSignedInHint()) return null;
  try {
    return window.localStorage.getItem(SIGNED_IN_HINT_ID) || null;
  } catch {
    return null;
  }
}
// `user` undefined with signedIn true (leaving for an OAuth provider) keeps
// whatever id was stored.
function setSignedInHint(signedIn: boolean, user?: { id: string } | null): void {
  // (not from the sign-in window a framed game opens: its session is only
  // passing through, see shared/auth-frame.ts)
  if (typeof window !== "undefined" && window.location.pathname === "/auth/popup") return;
  try {
    window.localStorage.setItem(SIGNED_IN_HINT, signedIn ? "1" : "0");
    if (!signedIn) window.localStorage.removeItem(SIGNED_IN_HINT_ID);
    else if (user) window.localStorage.setItem(SIGNED_IN_HINT_ID, user.id);
  } catch {
    /* storage blocked; the async check still corrects the UI shortly */
  }
}

// OAuth + magic link return the user to the page they signed in FROM (Supabase
// parses the tokens from the URL on load, detectSessionInUrl, and the SPA
// fallback serves any deep link). Computed at call time, not module load, so
// it reflects wherever the user actually is when they click Sign in.
// NOTE: the Supabase dashboard's auth Redirect URLs must allow deep paths
// (wildcard entries like https://achtungroyale.io/** and the localhost/preview
// equivalents), or Supabase silently falls back to the Site URL.
const redirectTo = () =>
  typeof window !== "undefined"
    ? window.location.origin + window.location.pathname + window.location.search
    : undefined;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // (seeded from the session stored on this device, so the first frame is
  // already signed in: no "Sign in" flashing before your name)
  const [session, setSession] = useState<Session | null>(() => storedSession());
  const [loading, setLoading] = useState(() => AUTH_ENABLED && !storedSession());

  useEffect(() => {
    if (!supabase) return;
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session?.user) {
        // Adopt the account name (once per account), then sync account settings.
        syncAccountNameToLocal(data.session.user);
        void startAccountSync(data.session.user);
      }
      setSignedInHint(!!data.session?.user, data.session?.user);
      setPaidCosmeticsAllowed(!!data.session?.user);
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      if (next?.user) {
        // Only the transition into a signed-in state. SIGNED_IN also fires on
        // token refresh and tab focus, which would otherwise inflate this badly.
        if (!getSignedInHint()) track("Signed In");
        syncAccountNameToLocal(next.user);
        void startAccountSync(next.user);
      } else {
        clearAccountNameSync();
        stopAccountSync();
      }
      setSignedInHint(!!next?.user, next?.user);
      setPaidCosmeticsAllowed(!!next?.user);
      setSession(next);
      setLoading(false);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
      stopAccountSync();
    };
  }, []);

  // If auth isn't configured, hand callers the inert value verbatim.
  if (!AUTH_ENABLED || !supabase) {
    return <AuthContext.Provider value={INERT}>{children}</AuthContext.Provider>;
  }

  const client = supabase;

  async function oauth(provider: "google" | "discord" | "facebook"): Promise<{ error?: string }> {
    const { error } = await client.auth.signInWithOAuth({ provider, options: { redirectTo: redirectTo() } });
    // We're leaving for the provider: flip the optimistic hint NOW, so the
    // page we return to renders the signed-in chrome from its first paint
    // (empty auth slot -> avatar) instead of flashing the Sign in button
    // during the session check. Backing out at the provider self-corrects:
    // the resolved (still signed-out) session flips the hint right back.
    if (!error) setSignedInHint(true);
    return error ? { error: error.message } : {};
  }

  const value: AuthContextValue = {
    enabled: true,
    user: session?.user ?? null,
    session,
    loading,
    signInWithGoogle: () => oauth("google"),
    signInWithDiscord: () => oauth("discord"),
    signInWithFacebook: () => oauth("facebook"),
    signInWithMagicLink: async (email: string) => {
      const { error } = await client.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo() },
      });
      return error ? { error: error.message } : {};
    },
    signOut: async () => {
      await client.auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
